import { FC, useState, useEffect } from 'react'
import { Modal, Form, Input, Button, Alert, Typography, Result } from 'antd'
import { LockOutlined, CloseOutlined, CheckCircleTwoTone } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { PasswordStrengthIndicator } from '../ui/PasswordStrengthIndicator'
import './auth-modal.css'

const { Title, Text } = Typography

interface ChangePasswordModalProps {
  visible: boolean
  onClose: () => void
}

export const ChangePasswordModal: FC<ChangePasswordModalProps> = ({
  visible,
  onClose
}) => {
  const { t } = useTranslation(['common', 'auth'])
  const { updatePassword, changePasswordState, clearChangePasswordState } = useAuth()
  const [form] = Form.useForm()
  const [newPassword, setNewPassword] = useState('')

  const handleSubmit = async (values: {
    currentPassword?: string
    newPassword: string
    confirmPassword: string
  }) => {
    const { success } = await updatePassword(values.newPassword)
    if (success) {
      form.resetFields()
    }
  }

  const handleCancel = () => {
    clearChangePasswordState()
    form.resetFields()
    setNewPassword('')
    onClose()
  }

  const handleClose = () => {
    if (changePasswordState.success) {
      clearChangePasswordState()
      form.resetFields()
      setNewPassword('')
    }
    onClose()
  }

  // 成功后自动关闭
  useEffect(() => {
    if (changePasswordState.success) {
      const timer = setTimeout(() => {
        handleClose()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [changePasswordState.success])

  return (
    <Modal
      open={visible}
      onCancel={changePasswordState.success ? handleClose : handleCancel}
      footer={null}
      width={changePasswordState.success ? 400 : 450}
      centered
      closable={!changePasswordState.success}
      maskClosable={!changePasswordState.success}
      closeIcon={!changePasswordState.success ? <CloseOutlined className="auth-modal__close-icon" /> : null}
    >
      {/* 密码修改成功状态 */}
      {changePasswordState.success ? (
        <Result
          icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
          status="success"
          title={t('auth:changePassword.success')}
          subTitle={t('auth:changePassword.successDescription')}
        />
      ) : (
        <>
          <div className="auth-modal__header">
            <Title level={2} className="auth-modal__title">
              {t('auth:changePassword.title')}
            </Title>
            <Text type="secondary" className="auth-modal__subtitle">
              {t('auth:changePassword.subtitle')}
            </Text>
          </div>

          {changePasswordState.error && (
            <Alert
              message={changePasswordState.error}
              type="error"
              showIcon
              className="auth-modal__alert"
              closable
              onClose={clearChangePasswordState}
            />
          )}

          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="currentPassword"
              label={t('auth:changePassword.currentPassword')}
              rules={[
                { required: true, message: t('auth:validation.currentPasswordRequired') }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="auth-modal__input-icon" />}
                placeholder={t('auth:changePassword.placeholder.currentPassword')}
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label={t('auth:changePassword.newPassword')}
              rules={[
                { required: true, message: t('auth:validation.newPasswordRequired') },
                { min: 8, message: t('auth:validation.passwordMinLength') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('currentPassword') !== value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error(t('auth:validation.sameAsOldPassword')))
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="auth-modal__input-icon" />}
                placeholder={t('auth:changePassword.placeholder.newPassword')}
                autoComplete="new-password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Form.Item>

            {newPassword && <PasswordStrengthIndicator password={newPassword} />}

            <Form.Item
              name="confirmPassword"
              label={t('auth:changePassword.confirmPassword')}
              dependencies={['newPassword']}
              rules={[
                { required: true, message: t('auth:validation.confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error(t('auth:validation.passwordMismatch')))
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="auth-modal__input-icon" />}
                placeholder={t('auth:changePassword.placeholder.confirmPassword')}
                autoComplete="new-password"
              />
            </Form.Item>

            
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={changePasswordState.loading}
                className="auth-modal__submit"
              >
                {t('auth:changePassword.submit')}
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  )
}
